import * as yup from 'yup'
import { useMemo } from 'react'
import { toast } from 'react-toastify'
import { Add } from '@mui/icons-material'
import { yupResolver } from '@hookform/resolvers/yup'
import { FormProvider, useForm } from 'react-hook-form'
import { Box, CircularProgress, Typography } from '@mui/material'
import { Form } from '../Form/Form'
import { Input } from '../Form/Input'
import { Button } from '../Form/Button'
import { useContract } from '~/contexts'
import { RegisteredAnnotation } from './components'
import { AttachedFiles } from '~/components/Annotations/components'
import { ContractModel, EContractStatus, useContractAddObservationMutation } from '~/graphql/types'
import { usePermission } from '~/hooks'
import { uploadToS3 } from '~/services/uploadFile'

export type AnnotationForm = {
  annotation: string
  file: File
}

const schema = yup.object().shape({
  annotation: yup.string(),
})

export type ContractAnnotationsProps = {
  contract: ContractModel
  customUpdateProposal?: Function
}
export const ContractAnnotations: React.FC<ContractAnnotationsProps> = ({ contract, customUpdateProposal }) => {
  const { notes } = contract
  const { setContract } = useContract()
  const [addAnnotationToContract, { loading }] = useContractAddObservationMutation()

  // const disabledFields = isDisabledContractUpdate && (contract?.status === EContractStatus.active || contract?.status === EContractStatus.cancellationRequested || contract?.status === EContractStatus.inDeactivation)

  const canAddAnnotations = usePermission(['contracts.updated'])

  const isDeactivated = contract?.status === EContractStatus.deactivated || contract?.status === EContractStatus.inDeactivation || contract?.status === EContractStatus.cancellationRequested

  const renderedAnnotations = useMemo(() => {
    return notes?.map((item, index) => (
      <RegisteredAnnotation
        key={`annotation-${index}`}
        annotation={item.text}
        createdAt={item.createdAt}
        analystName={item.analystName}
        file={item.file}
      />
    ))
  }, [notes])

  const methods = useForm<AnnotationForm>({
    resolver: yupResolver(schema),
    defaultValues: {
      annotation: '',
      file: undefined
    }
  })

  const attachedFile = methods.watch('file')

  const onSubmit = async (formData: AnnotationForm) => {
    const hasAnnotation = formData.annotation?.trim()
    const hasFile = formData.file !== undefined && formData.file !== null
    if (!hasAnnotation && !hasFile) {
      methods.setError('annotation', {
        type: 'manual',
        message: 'Adicione uma observação ou anexe um arquivo'
      })
      return
    }

    try {
      let uploadedFileKey: string | undefined

      if (formData.file) {
        const uploadResult = await uploadToS3([formData.file])
        uploadedFileKey = uploadResult.key ?? undefined
      }

      const { data: updatedContract } = await addAnnotationToContract({
        variables: {
          params: {
            _id: contract._id,
            text: formData.annotation || '',
            fileKey: uploadedFileKey
          }
        }
      })

      methods.reset()

      if (customUpdateProposal) {
        updatedContract && customUpdateProposal(updatedContract.contractAddObservation as ContractModel)
        return
      }

      updatedContract && setContract(updatedContract.contractAddObservation as ContractModel)
      toast.success('Observação registrada com sucesso')
    } catch (err) {
      toast.error('Houve um problema ao adicionar observação')
    }
  }

  return (
    <FormProvider {...methods}>
      <Form key='annotations'>
        <Box flexDirection='column' display='flex' gap={3}>
          <Typography variant='h3' fontWeight={500} marginTop={'1rem'}>Observações</Typography>

          {renderedAnnotations}

          <Box sx={{
            position: 'relative',
            '& .MuiFormControl-root': {
              marginBottom: 0
            }
          }}>
            <Input
              multiline
              minRows={5}
              name='annotation'
              disabled={loading || !canAddAnnotations}
              placeholder='Escreva aqui suas observações'
              sx={{
                marginBottom: 0,
                '& .MuiInputBase-root': {
                  paddingBottom: attachedFile ? '80px' : '48px',
                }
              }}
            />

            <Box sx={{
              position: 'absolute',
              left: 0,
              right: 0,
              bottom: 0,
              top: 0,
              pointerEvents: 'none',
              '& > *': {
                pointerEvents: 'auto'
              }
            }}>
              <AttachedFiles disabled={!canAddAnnotations} loading={loading} />
            </Box>
          </Box>

          <Button
            color='secondary'
            style={{ alignSelf: 'start' }}
            disabled={loading || !canAddAnnotations || isDeactivated}
            onClick={methods.handleSubmit(onSubmit)}
            startIcon={loading ? <CircularProgress color='inherit' size={24} /> : <Add />}
          >
            Registrar observação
          </Button>
        </Box>
      </Form>
    </FormProvider>
  )
}
